import ExpandableMessages from "common/components/Elements/ExpandableMessages/ExpandableMessages.tsx";
import type { Notification } from "core/redux/reducers/notification/notificationReducer.types";
import type { ToastOptions } from "react-toastify";
import type { Message } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import { notify } from "widgets";

// Define all possible severity types
export type SeverityType = "INFO" | "WARN" | "ERROR" | "DEBUG" | "DETAIL";

// Define the mapping for notification methods - this is the key addition
type NotifyMethodKey = keyof Omit<typeof notify, "default" | "withJSX" | "update" | "clearWaitingQueue">;

/**
 * Interface for grouped messages
 */
export interface GroupedMessages {
  INFO: Message[];
  WARN: Message[];
  ERROR: Message[];
  DEBUG: Message[];
  DETAIL: Message[];
}

/**
 * Groups messages in a notification object by their severity
 * @param notification The notification object
 * @returns Object with messages grouped by severity
 */
export function groupMessagesBySeverity(notification: Notification): GroupedMessages {
  const result: GroupedMessages = {
    INFO: [],
    WARN: [],
    ERROR: [],
    DEBUG: [],
    DETAIL: [],
  };

  const messages = notification.messages || [];

  messages.forEach((message) => {
    if (message.severity in result) {
      result[message.severity as SeverityType].push(message);
    }
  });

  return result;
}

/**
 * Configuration object for displayNotifications
 */
export interface NotifyConfig {
  maxShown?: number;
  titleOverrides?: Partial<Record<SeverityType, string>>;
}

// Default titles for each severity
const defaultTitles: Record<SeverityType, string> = {
  INFO: "Info",
  WARN: "Warning",
  ERROR: "Error",
  DEBUG: "Debug",
  DETAIL: "Detail",
};

// Map of severity to notification method
const notifyMethods: Partial<Record<SeverityType, NotifyMethodKey>> = {
  INFO: "info",
  WARN: "warning",
  ERROR: "error",
  DEBUG: "error",
  DETAIL: "info",
};

/**
 * Displays notifications grouped by severity using appropriate notify methods
 * @param notification The notification object
 * @param config Configuration for how to display notifications
 * @param opts
 */
export function displayNotifications(notification: Notification, config: NotifyConfig, opts: ToastOptions = {}): void {
  const { maxShown = 15, titleOverrides = {} } = config;

  const groupedNotifications = groupMessagesBySeverity(notification);

  Object.entries(groupedNotifications).forEach(([severity, messages]) => {
    if (messages.length > 0) {
      const severityType = severity as SeverityType;
      const notifyMethod = notifyMethods[severityType];

      if (notifyMethod && notify[notifyMethod]) {
        notify[notifyMethod](
          {
            title: titleOverrides[severityType] || defaultTitles[severityType],
            children: <ExpandableMessages messages={messages} maxShown={maxShown} />,
          },
          { ...opts },
        );
      }
    }
  });
}
